import { useCallback, Dispatch, SetStateAction } from 'react';
import { GameState, TrainingFocus, Position } from '../../../domain/entities';
import { getPlayerTeam, findPlayerInState, addNewsItem } from '../../../services/GameService';
import { TRAINING_FACILITIES_LEVELS } from '../../../domain/constants';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;

interface UseTrainingManagementProps {
    setGameState: SetGameState;
}

export const useTrainingManagement = ({ setGameState }: UseTrainingManagementProps) => {
    const updateTeamTrainingFocus = useCallback((focus: TrainingFocus) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);
            if (playerTeam) {
                playerTeam.trainingFocus = focus;
            }
            return newState;
        });
    }, [setGameState]);

    const updatePositionalTrainingFocus = useCallback((position: Position, focus: TrainingFocus | null) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);
            if (playerTeam) {
                playerTeam.positionalTrainingFocus[position] = focus;
            }
            return newState;
        });
    }, [setGameState]);
    
    const updatePlayerTrainingFocus = useCallback((playerId: number, focus: TrainingFocus | null) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;

            const playerInfo = findPlayerInState(newState, playerId);
            if (playerInfo) {
                playerInfo.player.trainingFocus = focus;
                if (playerInfo.team.name === newState.playerTeamName) {
                    const focusText = focus ? `${focus} training` : 'the general team focus';
                    addNewsItem(newState, 'Training Focus Changed', `${playerInfo.player.name} has switched their individual training to ${focusText}.`, 'general', undefined, { type: 'playerDetail', title: `Player: ${playerInfo.player.name}`, playerId: playerInfo.player.id });
                }
            }
            return newState;
        });
    }, [setGameState]);

    const upgradeTrainingFacilities = useCallback((): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }
                
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
                const currentLevel = playerTeam.trainingFacilities.level;

                if (currentLevel >= TRAINING_FACILITIES_LEVELS.length) {
                    resolve({ success: false, message: "Facilities are already at maximum level." });
                    return prevState;
                }

                const upgradeInfo = TRAINING_FACILITIES_LEVELS[currentLevel];
                if (playerTeam.finances.balance < upgradeInfo.upgradeCost) {
                    resolve({ success: false, message: "Not enough funds to upgrade facilities." });
                    return prevState;
                }

                playerTeam.trainingFacilities.level++;
                playerTeam.finances.balance -= upgradeInfo.upgradeCost;
                playerTeam.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Upgraded Training Facilities to Level ${playerTeam.trainingFacilities.level}`, amount: -upgradeInfo.upgradeCost });
                
                resolve({ success: true, message: `Training facilities upgraded to Level ${playerTeam.trainingFacilities.level}!`});
                return newState;
            });
        });
    }, [setGameState]);

    const downgradeTrainingFacilities = useCallback((): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }
                
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
                const currentLevel = playerTeam.trainingFacilities.level;

                if (currentLevel <= 1) {
                    resolve({ success: false, message: "Facilities are already at the minimum level." });
                    return prevState;
                }

                playerTeam.trainingFacilities.level--;
                addNewsItem(newState, 'Facilities Downgraded', `The board has downgraded the training facilities to Level ${playerTeam.trainingFacilities.level} to reduce staff wage costs.`, 'finance');
                
                resolve({ success: true, message: `Training facilities downgraded to Level ${playerTeam.trainingFacilities.level}.`});
                return newState;
            });
        });
    }, [setGameState]);

    return {
        updateTeamTrainingFocus,
        updatePositionalTrainingFocus,
        updatePlayerTrainingFocus,
        upgradeTrainingFacilities,
        downgradeTrainingFacilities,
    };
};
